import {
  defaultConflictHandler,
  type RxCollection,
  type RxConflictHandler,
} from "rxdb";
import type { Invoice } from "../collections/invoice";
import {
  type InvoiceConflictCase,
  type InvoiceConflictCaseSnapshot,
  RXDB_INVOICE_CONFLICT_CASE_COLLECTION_NAME,
} from "../collections/invoice-conflict-case";

const RXDB_RESOLVE_CONFLICT_CONTEXT = "replication-resolve-conflict";

export const buildInvoiceConflictCaseId = (
  losingLocal: InvoiceConflictCaseSnapshot,
  winner: InvoiceConflictCaseSnapshot,
  assumedMaster: InvoiceConflictCaseSnapshot | null,
): string =>
  assumedMaster
    ? `${losingLocal.id}:${assumedMaster.meta.updatedAt}`
    : `${losingLocal.id}:no-master:${winner.meta.updatedAt}`;

export const upsertInvoiceConflictCase = async ({
  collection,
  losingLocal,
  winner,
  assumedMaster,
}: {
  collection: RxCollection<InvoiceConflictCase>;
  losingLocal: InvoiceConflictCaseSnapshot;
  winner: InvoiceConflictCaseSnapshot;
  assumedMaster: InvoiceConflictCaseSnapshot | null;
}): Promise<void> => {
  const id = buildInvoiceConflictCaseId(losingLocal, winner, assumedMaster);
  const existingCase = await collection.findOne(id).exec();

  if (existingCase) {
    await existingCase.incrementalPatch({ losingLocal, winner, assumedMaster });
    return;
  }

  await collection.insert({
    id,
    invoiceId: losingLocal.id,
    createdByUserId: losingLocal.meta.updatedByUserId,
    // Deliberate exception to the no-local-time standard: a local-only diagnostic
    // field, never replicated or compared across devices, and getCurrentTime()
    // throws until the first server exchange, which would wedge conflict
    // resolution on a clock problem rather than a data problem.
    detectedAt: new Date().toISOString(),
    losingLocal,
    winner,
    assumedMaster,
  });
};

export const createInvoiceConflictHandler =
  (
    getCaseCollection: () => RxCollection<InvoiceConflictCase> | null,
  ): RxConflictHandler<Invoice> =>
  async (input, context) => {
    const resolution = await defaultConflictHandler(input, context);

    if (context !== RXDB_RESOLVE_CONFLICT_CONTEXT || resolution.isEqual) {
      return resolution;
    }

    const collection = getCaseCollection();

    if (!collection) {
      throw new Error(
        `Cannot record a losing invoice: the ${RXDB_INVOICE_CONFLICT_CASE_COLLECTION_NAME} collection has not been registered`,
      );
    }

    await upsertInvoiceConflictCase({
      collection,
      losingLocal: input.newDocumentState,
      winner: input.realMasterState,
      assumedMaster: input.assumedMasterState ?? null,
    });

    return resolution;
  };

let registeredCaseCollection: RxCollection<InvoiceConflictCase> | null = null;

export const registerInvoiceConflictCaseCollection = (
  collection: RxCollection<InvoiceConflictCase> | null | undefined,
): void => {
  registeredCaseCollection = collection ?? null;
};

export const invoiceConflictHandler = createInvoiceConflictHandler(
  () => registeredCaseCollection,
);
